import React, { useState, useEffect, useRef } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Upload, Building2, Cpu, Wifi, CheckCircle2, AlertCircle, Loader2, Search, Database } from 'lucide-react';
import { importarClientesFacto, importarDispositivos4sat, importarTelcoSims, enriquecerRuts, estadoEnriquecimiento } from '../../lib/gestion4satApi';
import { toast } from 'sonner';

function ImportCard({ tipo, title, description, icon: Icon, color, importFn, fields, result, isLoading, onImport }) {
  return (
    <Card data-testid={`import-card-${tipo}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label>
          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={e => onImport(tipo, importFn, e.target.files[0])}
            disabled={isLoading}
            data-testid={`file-input-${tipo}`}
          />
          <div className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-emerald-500/50 transition-colors">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Procesando...</span>
              </div>
            ) : (
              <>
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click para seleccionar archivo Excel</p>
                <p className="text-xs text-muted-foreground mt-1">.xlsx</p>
              </>
            )}
          </div>
        </label>

        {result && !result.error && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
              <CheckCircle2 className="h-4 w-4" /> Importaci&oacute;n exitosa
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              {fields.map(f => (
                <div key={f.key}>
                  <span className="text-muted-foreground text-xs">{f.label}</span>
                  <div className="font-bold tabular-nums">{result[f.key] ?? 0}</div>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-red-500 cursor-pointer">
                  {result.errors.length} errores
                </summary>
                <div className="mt-1 text-xs font-mono max-h-32 overflow-y-auto bg-background p-2 rounded">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-red-500">{JSON.stringify(e)}</div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {result?.error && (
          <div className="bg-red-500/10 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
            <span className="text-sm text-red-600">{result.error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function G4sImportarPage() {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [enrichState, setEnrichState] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const pollRef = useRef(null);

  // Check enrichment status on mount
  useEffect(() => {
    checkEnrichStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const checkEnrichStatus = async () => {
    try {
      const estado = await estadoEnriquecimiento();
      setEnrichState(estado);
      if (estado.estado === 'en_proceso') startPolling();
    } catch { /* ignore */ }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const estado = await estadoEnriquecimiento();
        setEnrichState(estado);
        if (estado.estado !== 'en_proceso') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (estado.estado === 'completado') {
            toast.success(`Enriquecimiento completado: ${estado.actualizados} empresas actualizadas`);
          }
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  const handleEnrich = async () => {
    setEnrichLoading(true);
    try {
      const res = await enriquecerRuts();
      if (res.ok) {
        toast.success(res.message);
        setEnrichState({ estado: 'en_proceso', total: res.total, procesados: 0, actualizados: 0 });
        startPolling();
      } else {
        toast.info(res.message);
      }
    } catch (e) { toast.error(e.message); }
    setEnrichLoading(false);
  };

  const handleImport = async (tipo, importFn, file) => {
    if (!file) return;
    setLoading(prev => ({ ...prev, [tipo]: true }));
    try {
      const result = await importFn(file);
      setResults(prev => ({ ...prev, [tipo]: result }));
      toast.success('Importaci\u00f3n completada');
    } catch (e) {
      toast.error(e.message);
      setResults(prev => ({ ...prev, [tipo]: { error: e.message } }));
    }
    setLoading(prev => ({ ...prev, [tipo]: false }));
  };

  return (
    <G4sLayout>
      <div className="space-y-6" data-testid="g4s-importar-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Importar Datos</h1>
          <p className="text-sm text-muted-foreground">
            Carga los archivos Excel para poblar el sistema. Orden recomendado: 1) Clientes Facto, 2) Dispositivos 4sat, 3) SIMs Telco
          </p>
        </div>

        <div className="grid gap-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 rounded-lg text-sm text-amber-700 dark:text-amber-400">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Importa en orden: primero Clientes (establece RUTs), luego Dispositivos (cruza por RUT), finalmente SIMs (asigna ICCID por IMEI).</span>
          </div>

          <ImportCard
            tipo="clientes"
            title="1. Clientes Facto"
            description="Archivo de clientes exportado desde Facto. Dedup por RUT, consolida m&uacute;ltiples contactos/emails."
            icon={Building2}
            color="bg-emerald-500"
            importFn={importarClientesFacto}
            fields={[
              { key: 'created', label: 'Nuevos' },
              { key: 'updated', label: 'Actualizados' },
              { key: 'total_ruts', label: 'RUTs \u00fanicos' },
            ]}
            result={results.clientes}
            isLoading={loading.clientes}
            onImport={handleImport}
          />

          <ImportCard
            tipo="dispositivos"
            title="2. Dispositivos 4sat"
            description="Historial de dispositivos GPS. Cruza autom&aacute;ticamente con empresas por RUT."
            icon={Cpu}
            color="bg-blue-500"
            importFn={importarDispositivos4sat}
            fields={[
              { key: 'created', label: 'Nuevos' },
              { key: 'updated', label: 'Actualizados' },
              { key: 'stock', label: 'Sin empresa (Stock)' },
            ]}
            result={results.dispositivos}
            isLoading={loading.dispositivos}
            onImport={handleImport}
          />

          <ImportCard
            tipo="sims"
            title="3. SIMs Telco (ExportedDevices)"
            description="Archivo de conectividad. Asigna ICCID y estado (Online/Offline) por IMEI. Detecta SIMs fantasma."
            icon={Wifi}
            color="bg-purple-500"
            importFn={importarTelcoSims}
            fields={[
              { key: 'matched', label: 'Emparejados' },
              { key: 'unmatched', label: 'Sin match' },
              { key: 'phantom_sims', label: 'SIMs fantasma' },
            ]}
            result={results.sims}
            isLoading={loading.sims}
            onImport={handleImport}
          />

          {/* Enriquecimiento SII */}
          <Card data-testid="enrich-card-sii">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                  <Search className="h-4 w-4 text-white" />
                </div>
                Enriquecer Razones Sociales (SII)
              </CardTitle>
              <CardDescription>
                Consulta masiva al SII para completar automáticamente las razones sociales faltantes en empresas importadas.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={handleEnrich}
                disabled={enrichLoading || enrichState?.estado === 'en_proceso'}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="btn-enriquecer-sii"
              >
                {enrichLoading || enrichState?.estado === 'en_proceso' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Database className="h-4 w-4 mr-2" />
                )}
                {enrichState?.estado === 'en_proceso' ? 'Procesando...' : 'Iniciar Enriquecimiento'}
              </Button>

              {enrichState?.estado === 'en_proceso' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className="font-mono font-medium">{enrichState.procesados}/{enrichState.total}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2.5">
                    <div
                      className="bg-amber-500 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${enrichState.total > 0 ? (enrichState.procesados / enrichState.total) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>Actualizados: <strong className="text-green-600">{enrichState.actualizados || 0}</strong></span>
                    <span>No encontrados: <strong>{enrichState.no_encontrados || 0}</strong></span>
                    <span>Errores: <strong className="text-red-500">{enrichState.errores || 0}</strong></span>
                  </div>
                </div>
              )}

              {enrichState?.estado === 'completado' && (
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                    <CheckCircle2 className="h-4 w-4" /> Enriquecimiento completado
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground text-xs">Total</span>
                      <div className="font-bold tabular-nums">{enrichState.total}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">Actualizados</span>
                      <div className="font-bold tabular-nums text-green-600">{enrichState.actualizados}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">No encontrados</span>
                      <div className="font-bold tabular-nums">{enrichState.no_encontrados}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">Errores</span>
                      <div className="font-bold tabular-nums text-red-500">{enrichState.errores}</div>
                    </div>
                  </div>
                  {enrichState.detalle?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer">
                        Últimos {enrichState.detalle.length} resultados
                      </summary>
                      <div className="mt-1 text-xs font-mono max-h-40 overflow-y-auto bg-background p-2 rounded space-y-1">
                        {enrichState.detalle.map((d, i) => (
                          <div key={i} className={d.razon_social ? 'text-green-600' : 'text-muted-foreground'}>
                            {d.rut}: {d.razon_social || d.error || 'No encontrado'} ({d.source})
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {enrichState.iniciado_por && `Iniciado por ${enrichState.iniciado_por} · `}
                    {enrichState.finished_at && new Date(enrichState.finished_at).toLocaleString('es-CL')}
                  </p>
                </div>
              )}

              {enrichState?.estado === 'sin_datos' && (
                <p className="text-sm text-muted-foreground">
                  Aún no se ha ejecutado ningún enriquecimiento. Importa clientes primero y luego ejecuta este proceso.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </G4sLayout>
  );
}
